/**
 * Applicant Form_Renderer — FieldRenderer
 * (application-form-builder spec, task 23.1).
 *
 * Renders one Form_Field inside the React Hook Form context: the accessible
 * label / help / error *slots* that wrap every control identically, plus the
 * control itself resolved from the Field_Registry (CUSTOM fields) or a bound
 * seam (BOUND fields). It is the single place the WCAG-AA wiring lives, so each
 * control gets:
 *   - a `<label htmlFor>` carrying the field label and a required marker;
 *   - an optional help-text region (`<controlId>-help`);
 *   - an error region (`<controlId>-error`, `role="alert"`);
 *   - `aria-describedby` stitching help + error onto the control.
 *
 * The control is bound through a React Hook Form `Controller`, so its value and
 * `onChange` flow through the form derived from `deriveZodSchema`. The error
 * slot surfaces the field's current React Hook Form message (Req 5.2, 5.5). For
 * LONG_TEXT fields with a configured word or character limit, a live counter
 * affordance is rendered beneath the control; its "over the limit" state comes
 * from the shared `validateValue` rules so it can never disagree with the
 * message that blocks submission (Req 5.4). The concrete BOUND controls
 * (Division selector, Title preferences, Contact identity, Document slot) are
 * task 23.3 — this component leaves a clean seam for them.
 *
 * Validates: Requirements 5.2, 5.4, 5.5
 */

import * as React from 'react';
import { Controller, type Control } from 'react-hook-form';

import { Label } from '@/components/ui/label';
import { RichHelpText } from '@/components/application-form/RichHelpText';
import type { ApplicationResponseValues } from '@/lib/application-form/deriveZodSchema';

import { getFieldControl } from './fieldRegistry';
import { BoundFieldControl } from './boundControls';
import { FieldType, type FormFieldDef, type ResponseValue } from './pure';
import { longTextLimitState } from './validationMessaging';

interface FieldRendererProps {
  /** The field to render (CUSTOM or BOUND). */
  field: FormFieldDef;
  /** The React Hook Form control for the applicant's response values. */
  control: Control<ApplicationResponseValues>;
  /** Whether the field is rendered read-only (e.g. an approval-locked page). */
  disabled?: boolean;
}

/** The required marker, hidden from assistive tech (the label also sets `aria-required`). */
function RequiredMark(): React.ReactElement {
  return (
    <span className="ml-0.5 text-destructive-text" aria-hidden="true">
      *
    </span>
  );
}

/**
 * The label + help + error scaffold shared by every field, parameterised by the
 * control rendered in the middle. `labelFor` is the control id for single
 * controls; group controls reference `labelId` via `aria-labelledby` instead.
 */
function FieldShell({
  field,
  controlId,
  labelId,
  helpId,
  errorId,
  errorMessage,
  children,
}: {
  field: FormFieldDef;
  controlId: string;
  labelId: string;
  helpId: string;
  errorId: string;
  errorMessage?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <Label id={labelId} htmlFor={controlId} className="text-body font-medium text-foreground">
        <span>
          {field.label}
          {field.required ? <RequiredMark /> : null}
        </span>
      </Label>

      <RichHelpText
        id={helpId}
        format={field.helpTextFormat}
        helpText={field.helpText}
        helpTextHtml={field.helpTextHtml}
        className="text-body-sm text-muted-foreground"
      />

      {children}

      {errorMessage ? (
        <p id={errorId} role="alert" className="text-body-sm text-destructive-text">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A live word/character counter for a LONG_TEXT field (Req 5.4). The counts are
 * display figures; the "over the limit" state is decided by the shared
 * `validateValue` rules (via `longTextLimitState`), so the counter and the
 * blocking validation message can never disagree. Announced politely so screen
 * readers hear the running total without stealing focus, and referenced from the
 * control's `aria-describedby`.
 */
function LongTextLimitMeter({
  field,
  value,
  meterId,
}: {
  field: FormFieldDef;
  value: ResponseValue | undefined;
  meterId: string;
}): React.ReactElement | null {
  const state = longTextLimitState(field, value);
  if (!state.hasWordLimit && !state.hasCharLimit) {
    return null;
  }
  const overLimit = state.overWordLimit || state.overCharLimit;
  return (
    <div id={meterId} aria-live="polite" className="space-y-0.5">
      <p className="flex flex-wrap gap-x-3 text-body-sm text-muted-foreground">
        {state.hasWordLimit ? (
          <span className={state.overWordLimit ? 'text-destructive-text' : undefined}>
            {state.wordCount} / {state.wordLimit} words
          </span>
        ) : null}
        {state.hasCharLimit ? (
          <span className={state.overCharLimit ? 'text-destructive-text' : undefined}>
            {state.charCount} / {state.characterLimit} characters
          </span>
        ) : null}
      </p>
      {/*
        Advisory soft cap (never a hard block): over-limit content still saves
        (the draft path no longer rejects it), so reassure the applicant their
        work is not lost and nudge them to trim it. For the pre-approval flow the
        submit gate still enforces the limit; for an APPROVED contestant editing
        Phase 2 (auto-save, no submit step) this is the only limit feedback, so
        it must read as guidance rather than an error.
      */}
      {overLimit ? (
        <p className="text-body-sm text-destructive-text">
          This is over the limit, but your changes are still saved. Please shorten it when you can.
        </p>
      ) : null}
    </div>
  );
}

/** Whether a LONG_TEXT field has a configured word or character limit. */
function hasLongTextLimit(field: FormFieldDef): boolean {
  if (field.fieldType !== FieldType.LONG_TEXT) {
    return false;
  }
  const word = field.wordLimit;
  const char = field.characterLimit;
  return (
    (typeof word === 'number' && Number.isInteger(word) && word > 0) ||
    (typeof char === 'number' && Number.isInteger(char) && char > 0)
  );
}

/**
 * Render a single field. CUSTOM fields bind a Field_Registry control through a
 * React Hook Form `Controller`; BOUND fields render the concrete bound control
 * for their Binding_Target (task 23.3) via `BoundFieldControl`, which reads and
 * writes its canonical entity and stores nothing in the response values.
 */
export function FieldRenderer({ field, control, disabled }: FieldRendererProps): React.ReactElement {
  if (field.fieldCategory === 'BOUND') {
    return <BoundFieldControl field={field} disabled={disabled} />;
  }

  const controlId = field.id;
  const labelId = `${field.id}-label`;
  const helpId = `${field.id}-help`;
  const errorId = `${field.id}-error`;
  const meterId = `${field.id}-meter`;
  const showMeter = hasLongTextLimit(field);

  const Control = getFieldControl(field.fieldType);

  return (
    <Controller<ApplicationResponseValues>
      control={control}
      name={field.id}
      render={({ field: rhf, fieldState }) => {
        const hasError = Boolean(fieldState.error);
        const errorMessage = fieldState.error?.message;
        const describedBy =
          [field.helpText ? helpId : null, showMeter ? meterId : null, hasError ? errorId : null]
            .filter(Boolean)
            .join(' ') || undefined;

        return (
          <FieldShell
            field={field}
            controlId={controlId}
            labelId={labelId}
            helpId={helpId}
            errorId={errorId}
            errorMessage={errorMessage}
          >
            {Control ? (
              <Control
                field={field}
                value={rhf.value}
                onChange={rhf.onChange}
                onBlur={rhf.onBlur}
                disabled={disabled}
                invalid={hasError}
                controlId={controlId}
                labelId={labelId}
                describedBy={describedBy}
              />
            ) : (
              <p className="text-body-sm text-muted-foreground" role="note">
                This field type is not yet supported.
              </p>
            )}
            {showMeter ? (
              <LongTextLimitMeter field={field} value={rhf.value} meterId={meterId} />
            ) : null}
          </FieldShell>
        );
      }}
    />
  );
}

export default FieldRenderer;
